<template>
  <div class="my-6 rounded-sm border border-subtle bg-surface-elevation-1 p-5">
    <div class="pb-4 text-body-m-bold">{{ t('Reviews.product.review.embed.title') }}</div>
    <div class="flex flex-col gap-6 desktop:flex-row">
      <div
        class="flex min-w-0 items-center justify-center rounded-sm p-6 desktop:flex-1"
        :class="isDarkPreview ? 'bg-surface-inverse' : 'bg-surface'"
      >
        <img :src="previewSrc" alt="OMR Reviews" class="w-full max-w-lg" data-testid="review-embed-preview" />
      </div>
      <div class="flex min-w-0 flex-col gap-4 desktop:flex-1">
        <div>
          <div class="pb-1 text-body-s-bold">{{ t('Reviews.shared.media.widgets.htmlCodeTitle') }}</div>
          <div class="flex min-w-0 rounded-sm border border-subtle bg-surface p-1">
            <div class="mr-2 w-full overflow-hidden text-ellipsis whitespace-nowrap text-subtlest" data-testid="review-embed-code">
              {{ embedCode }}
            </div>
            <TooltipWrapper top>
              <template #trigger>
                <CopyIcon
                  class="mt-1 lucide-icon-s align-middle text-icon-primary hover:text-icon-selected"
                  data-testid="review-embed-copy"
                  @click="copyEmbedCode"
                />
              </template>
              <template #content>
                <div v-html="t('Reviews.shared.media.widgets.embedToolTip')"></div>
              </template>
            </TooltipWrapper>
          </div>
        </div>
        <div>
          <div class="pb-1 text-body-s-bold">{{ t('Reviews.shared.media.widgets.downloadSelectTitle') }}</div>
          <DropDownSelect class="rounded-sm bg-surface text-subtlest">
            <template #activator="{ open }">
              <button class="flex w-full items-center justify-between rounded-sm border border-subtle p-1" @click.prevent="open">
                <span>{{ selectedOption.text }}</span>
                <ChevronDownIcon class="icon-subtlest lucide-icon-s" />
              </button>
            </template>
            <template #default="{ close }">
              <ul class="m-0 list-none p-0">
                <li v-for="option in downloadOptions" :key="option.text">
                  <label class="block cursor-pointer">
                    <input
                      name="review-embed-format"
                      type="radio"
                      :value="option"
                      class="peer pointer-events-none absolute opacity-0"
                      :checked="selectedOption === option"
                      @change="selectedOption = option"
                    />
                    <span class="block min-w-20 px-3 py-2 peer-checked:bg-surface-elevation-1 hover:bg-surface-elevation-1" @click="close">
                      {{ option.text }}
                    </span>
                  </label>
                </li>
              </ul>
            </template>
          </DropDownSelect>
        </div>
        <div>
          <div class="pb-1 text-body-s-bold">{{ t('Reviews.product.review.embed.widgetTypeTitle') }}</div>
          <DropDownSelect class="rounded-sm bg-surface text-subtlest">
            <template #activator="{ open }">
              <button
                class="flex w-full items-center justify-between rounded-sm border border-subtle p-1"
                :aria-label="t('Reviews.product.review.embed.widgetTypeTitle')"
                @click.prevent="open"
              >
                <span>{{ t(selectedTheme.labelKey) }}</span>
                <ChevronDownIcon class="icon-subtlest lucide-icon-s" />
              </button>
            </template>
            <template #default="{ close }">
              <ul class="m-0 list-none p-0">
                <li v-for="theme in themes" :key="theme.template">
                  <label class="block cursor-pointer">
                    <input
                      name="review-embed-theme"
                      type="radio"
                      :value="theme.template"
                      class="peer pointer-events-none absolute opacity-0"
                      :checked="selectedTemplate === theme.template"
                      @change="selectedTemplate = theme.template"
                    />
                    <span
                      class="block min-w-20 px-3 py-2 peer-checked:bg-surface-elevation-1 hover:bg-surface-elevation-1"
                      :data-testid="`review-embed-theme-${theme.template}`"
                      @click="close"
                    >
                      {{ t(theme.labelKey) }}
                    </span>
                  </label>
                </li>
              </ul>
            </template>
          </DropDownSelect>
        </div>
        <a
          class="btn-primary-color-s self-start"
          :href="downloadLink"
          :download="downloadFilename"
          target="_blank"
          data-testid="review-embed-download"
        >
          {{ t('Reviews.shared.media.widgets.downloadButtonLabel') }} <DownloadIcon class="ml-2 lucide-icon-m" />
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { TooltipWrapper } from '@ramp106/omrjs-core-ui'
import { ChevronDownIcon, CopyIcon, DownloadIcon } from '@ramp106/omrjs-core-ui/icons'
import { reviewsPaths } from '@ramp106/omrjs-routing'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { baseOmrUrl } from '@/config/env'
import type { ProductData } from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper'

import type { SurveyResponseDetails } from './types'

interface EmbedTemplate {
  template: string
  labelKey: string
}

interface DownloadOption {
  text: string
  size: number
  format: 'svg' | 'png'
}

const props = defineProps<{
  product: Pick<ProductData, 'id' | 'slug' | 'title'>
  surveyResponse: SurveyResponseDetails
}>()

const { t, locale } = useI18n()

const themes: EmbedTemplate[] = [
  { template: 'light', labelKey: 'Reviews.shared.media.widgets.lightTitle' },
  { template: 'dark', labelKey: 'Reviews.shared.media.widgets.darkTitle' },
  { template: 'transparentLight', labelKey: 'Reviews.shared.media.widgets.transparentLightTitle' },
  { template: 'transparentDark', labelKey: 'Reviews.shared.media.widgets.transparentDarkTitle' },
]
const selectedTemplate = ref<string>('light')
const selectedTheme = computed(() => themes.find((theme) => theme.template === selectedTemplate.value) ?? themes[0])
const isDarkPreview = computed(() => selectedTemplate.value === 'dark' || selectedTemplate.value === 'transparentDark')

const downloadOptions: DownloadOption[] = [
  { text: 'SVG', size: 500, format: 'svg' },
  { text: 'PNG 500px', size: 500, format: 'png' },
  { text: 'PNG 1000px', size: 1000, format: 'png' },
  { text: 'PNG 2000px', size: 2000, format: 'png' },
]
const selectedOption = ref<DownloadOption>(downloadOptions[0])

const reviewSlug = computed(() => props.surveyResponse.review?.id ?? '')

const reviewPdpUrl = computed(
  () =>
    baseOmrUrl +
    reviewsPaths.product({ locale: locale.value, slug: props.product.slug }, { utm_source: 'review-embed', utm_medium: 'vendor' }),
)

const previewSrc = computed(
  () => baseOmrUrl + reviewsPaths.reviewWidget({ locale: locale.value, slug: reviewSlug.value }, { template: selectedTemplate.value }),
)

const escapeAttribute = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/'/g, '&#39;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const embedCode = computed(
  () =>
    `<a title='${escapeAttribute(props.product.title)}' href='${reviewPdpUrl.value}'><img src='${previewSrc.value}' alt='OMR Reviews' width='320' /></a>`,
)

const downloadLink = computed(
  () =>
    baseOmrUrl +
    reviewsPaths.reviewWidget(
      { locale: locale.value, slug: reviewSlug.value },
      selectedOption.value.format === 'svg'
        ? { template: selectedTemplate.value, format: 'svg' }
        : { template: selectedTemplate.value, format: 'png', width: selectedOption.value.size.toString() },
    ),
)

const downloadFilename = computed(() => `omr-review-${reviewSlug.value}.${selectedOption.value.format}`)

function copyEmbedCode() {
  navigator.clipboard.writeText(embedCode.value)
  showNotification(t('Reviews.shared.media.widgets.embedCopied'), 'success')
}
</script>
